import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertBanner, Modal, Select, useLocale } from '@ministry/ui/internal';

import {
  assignRole,
  getRoleCatalogue,
  scopeRequiresAnchor,
  SCOPE_LEVELS,
  type AssignRoleResult,
} from '../../lib/admin-users.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { genericFieldErrorMessage } from '../../lib/field-errors.js';
import { scopeLevelLabel } from '../../lib/admin-lifecycle.js';
import { roleLabel } from '../../lib/roles.js';
import { useT } from '../../i18n/strings.js';
import { grantRefusalKey } from './routes.js';
import { OrgUnitPicker } from './OrgUnitPicker.js';

export interface GrantRoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  onSuccess: (result: AssignRoleResult) => void;
}

/**
 * SCREEN_SPECS.md I19 — "the important one" (task brief). `GET /roles` names which roles
 * THIS caller may grant (`grantable`) and this form offers ONLY those — never the full
 * nine-role vocabulary, matching `rolesGrantableBy`'s own stated reason: offering a role
 * the caller will then be refused for is worse than offering fewer.
 *
 * R-044: a `section`/`division`/`ministry` scope REQUIRES an org-unit anchor, enforced
 * HERE before the request is ever sent (`scope-anchor-rule.test.ts` is the server-side
 * mirror of this same rule) — never discovered from a 422.
 *
 * The five `grant-authority.ts` refusal codes (`error.details.refusal`) each render their
 * own specific, human, bilingual sentence — never a bare "Forbidden".
 */
export function GrantRoleModal({ open, onOpenChange, userId, userName, onSuccess }: GrantRoleModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [roleCode, setRoleCode] = React.useState('');
  const [scopeLevel, setScopeLevel] = React.useState('');
  const [scopeOrgUnitId, setScopeOrgUnitId] = React.useState<string | undefined>(undefined);
  const [clearanceCode, setClearanceCode] = React.useState('');
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  const catalogueQuery = useQuery({
    queryKey: ['admin-role-catalogue'],
    queryFn: ({ signal }) => getRoleCatalogue(signal),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) {
      setRoleCode('');
      setScopeLevel('');
      setScopeOrgUnitId(undefined);
      setClearanceCode('');
      setTouched({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => {
      const clearance = catalogueQuery.data?.clearanceLevels.find((c) => c.code === clearanceCode);
      return assignRole(userId, {
        roleCode,
        scopeLevel,
        scopeOrgUnitId: scopeRequiresAnchor(scopeLevel) ? scopeOrgUnitId ?? null : null,
        maxClearanceRank: clearance?.rank ?? 1,
      });
    },
    onSuccess: (result) => {
      onOpenChange(false);
      onSuccess(result);
    },
  });

  const anchorRequired = scopeLevel !== '' && scopeRequiresAnchor(scopeLevel);
  const platformNotSuperAdmin = scopeLevel === 'platform' && roleCode !== '' && roleCode !== 'super_admin';

  const roleError = touched.roleCode && roleCode === '' ? t('admin.grant.role.error') : undefined;
  const scopeError =
    touched.scopeLevel && scopeLevel === ''
      ? t('admin.grant.scope.error')
      : platformNotSuperAdmin
        ? t('admin.grant.scope.platformOnlySuperAdmin')
        : undefined;
  const anchorError = touched.scopeOrgUnitId && anchorRequired && !scopeOrgUnitId ? t('admin.grant.anchor.error') : undefined;
  const clearanceError = touched.clearanceCode && clearanceCode === '' ? t('admin.grant.clearance.error') : undefined;

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  const isValid = roleCode !== '' && scopeLevel !== '' && !platformNotSuperAdmin && (!anchorRequired || Boolean(scopeOrgUnitId)) && clearanceCode !== '';

  const handleConfirm = () => {
    setTouched({ roleCode: true, scopeLevel: true, scopeOrgUnitId: true, clearanceCode: true });
    if (!isValid) return;
    mutation.mutate();
  };

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) {
      return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    }
    if (mutation.error instanceof ApiError) {
      const refusal = mutation.error.details?.refusal;
      if (typeof refusal === 'string') {
        return <AlertBanner variant="critical" title={t(grantRefusalKey(refusal))} />;
      }
      if (mutation.error.code === 'NOT_FOUND') {
        return <AlertBanner variant="critical" title={t('admin.grant.error.userNotFound')} />;
      }
      const RENDERED = new Set(['roleCode', 'scopeLevel', 'scopeOrgUnitId', 'maxClearanceRank']);
      const unrenderable = mutation.error.fieldErrors.filter((fe) => !RENDERED.has(fe.field));
      if (unrenderable.length > 0) {
        return (
          <AlertBanner
            variant="critical"
            title={unrenderable.map((fe) => genericFieldErrorMessage(t, fe.code, fe.params)).join(' ')}
          />
        );
      }
      if (mutation.error.fieldErrors.length > 0) return <AlertBanner variant="critical" title={t('admin.error.checkFields')} />;
      return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
    }
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  const grantable = catalogueQuery.data?.grantable ?? [];
  const roleOptions = grantable.map((code) => ({ value: code, label: roleLabel(code, locale) }));

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.grant.title', { name: userName })}
      description={t('admin.grant.description')}
      confirmLabel={mutation.isPending ? t('admin.grant.submitting') : t('admin.grant.submit')}
      cancelLabel={t('admin.common.cancel')}
      loading={mutation.isPending}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-4">
        {banner}
        {catalogueQuery.isError ? <AlertBanner variant="critical" title={t('admin.grant.error.catalogueLoad')} /> : null}
        {grantable.length === 0 && catalogueQuery.isSuccess ? (
          <AlertBanner variant="warning" title={t('admin.grant.error.noneGrantable')} />
        ) : null}
        <Select
          id="admin-grant-role"
          label={t('admin.grant.role.label')}
          required
          value={roleCode || undefined}
          onValueChange={(value) => {
            setRoleCode(value);
            setTouched((prev) => ({ ...prev, roleCode: true }));
          }}
          loadingOptions={catalogueQuery.isLoading}
          options={roleOptions}
          errorText={roleError ?? fieldErrorFor('roleCode')}
          placeholder={t('admin.grant.role.placeholder')}
        />
        <Select
          id="admin-grant-scope"
          label={t('admin.grant.scope.label')}
          required
          value={scopeLevel || undefined}
          onValueChange={(value) => {
            setScopeLevel(value);
            setTouched((prev) => ({ ...prev, scopeLevel: true }));
          }}
          options={SCOPE_LEVELS.map((level) => ({ value: level, label: scopeLevelLabel(level, locale) }))}
          errorText={scopeError ?? fieldErrorFor('scopeLevel')}
          helperText={t('admin.grant.scope.hint')}
          placeholder={t('admin.grant.scope.placeholder')}
        />
        {anchorRequired ? (
          <OrgUnitPicker
            id="admin-grant-anchor"
            label={t('admin.grant.anchor.label')}
            required
            value={scopeOrgUnitId}
            onValueChange={(value) => {
              setScopeOrgUnitId(value);
              setTouched((prev) => ({ ...prev, scopeOrgUnitId: true }));
            }}
            errorText={anchorError ?? fieldErrorFor('scopeOrgUnitId')}
            helperText={t('admin.grant.anchor.hint')}
          />
        ) : null}
        <Select
          id="admin-grant-clearance"
          label={t('admin.grant.clearance.label')}
          required
          value={clearanceCode || undefined}
          onValueChange={(value) => {
            setClearanceCode(value);
            setTouched((prev) => ({ ...prev, clearanceCode: true }));
          }}
          loadingOptions={catalogueQuery.isLoading}
          options={(catalogueQuery.data?.clearanceLevels ?? []).map((level) => ({
            value: level.code,
            label: `${locale === 'ne' ? level.labelNe : level.labelEn} (${level.rank})`,
          }))}
          errorText={clearanceError ?? fieldErrorFor('maxClearanceRank')}
          placeholder={t('admin.grant.clearance.placeholder')}
        />
      </div>
    </Modal>
  );
}
